import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { REF_IMAGE_OPEN_EVENT } from './Editor/RefImage';
import { VISUAL_TYPE_META, normalizeVisualType } from '../../shared/visualTypes';

// The enlarged reference image (2B, mock-up 06 §02). One component at every
// width: dark scrim, the image as large as fits, its caption and the row it
// belongs to. Arrow keys and swipes move between images; ×, Escape, the scrim
// or a downward swipe close it, and focus returns to the image that opened it.

interface Slide {
  figure: HTMLElement;
  src: string;
  alt: string;
  caption: string;
  rowLabel: string;
}

function slideFor(figure: HTMLElement): Slide {
  const img = figure.querySelector('img');
  const root = figure.closest('.ProseMirror');
  const row = figure.closest('.script-row');
  const rows = root ? Array.from(root.querySelectorAll('.script-row')) : [];
  const number = row ? rows.indexOf(row) + 1 : 0;
  const type = normalizeVisualType(figure.closest('.script-cell')?.getAttribute('data-visual-type'));
  const parts = [number > 0 ? `Row ${number}` : null, type ? VISUAL_TYPE_META[type].label : null].filter(Boolean);
  return {
    figure,
    src: img?.currentSrc || img?.getAttribute('src') || '',
    alt: img?.alt ?? '',
    caption: figure.getAttribute('data-caption') ?? '',
    rowLabel: parts.join(' · '),
  };
}

export default function RefImageLightbox() {
  const [slides, setSlides] = useState<Slide[] | null>(null);
  const [index, setIndex] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const figure = (event as CustomEvent<{ figure: HTMLElement }>).detail?.figure;
      if (!figure) return;
      const root = figure.closest('.ProseMirror');
      const all = root
        ? Array.from(root.querySelectorAll<HTMLElement>('figure.ref-figure'))
        : [figure];
      setSlides(all.map(slideFor));
      setIndex(Math.max(0, all.indexOf(figure)));
    };
    document.addEventListener(REF_IMAGE_OPEN_EVENT, onOpen);
    return () => document.removeEventListener(REF_IMAGE_OPEN_EVENT, onOpen);
  }, []);

  const close = useCallback(() => {
    const opener = slides?.[index]?.figure.querySelector<HTMLElement>('.ref-frame');
    setSlides(null);
    window.setTimeout(() => opener?.focus(), 0);
  }, [slides, index]);

  const step = useCallback(
    (delta: number) => {
      if (!slides) return;
      setIndex((i) => Math.min(slides.length - 1, Math.max(0, i + delta)));
    },
    [slides],
  );

  useEffect(() => {
    if (!slides) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [slides, close, step]);

  if (!slides || slides.length === 0) return null;
  const slide = slides[index]!;

  return createPortal(
    <div
      className="ref-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${index + 1} of ${slides.length}`}
      data-print-hide
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      onTouchStart={(e) => {
        const t = e.touches[0];
        if (t) touchRef.current = { x: t.clientX, y: t.clientY };
      }}
      onTouchEnd={(e) => {
        const start = touchRef.current;
        const t = e.changedTouches[0];
        touchRef.current = null;
        if (!start || !t || e.touches.length > 0) return;
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) close();
        else if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      }}
    >
      <div className="ref-lightbox-top">
        <span className="ref-lightbox-count">
          Image {index + 1} of {slides.length}
        </span>
        <button ref={closeRef} type="button" className="ref-lightbox-close" aria-label="Close" onClick={close}>
          ×
        </button>
      </div>
      <div
        className="ref-lightbox-img"
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <img src={slide.src} alt={slide.alt} />
      </div>
      <div className="ref-lightbox-cap">
        {slide.rowLabel && <span className="ref-lightbox-row">{slide.rowLabel}</span>}
        {slide.caption}
      </div>
      {slides.length > 1 && (
        <div className="ref-lightbox-nav">
          <button type="button" disabled={index === 0} onClick={() => step(-1)}>
            ← Previous
          </button>
          <button type="button" disabled={index === slides.length - 1} onClick={() => step(1)}>
            Next →
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
